import type { EventMap } from './types';

/**
 * EventBus class for managing event listeners and dispatching events.
 * Requires an event map to define the events and their corresponding listener types.
 */
export class EventBus<Events extends EventMap = Record<string, never>> {
  private listeners = new Map<keyof Events, Set<Events[keyof Events]>>();

  /**
   * Adds a listener for a specific event.
   * @param event - The event name.
   * @param listener - The listener function to add.
   * @returns A function to remove the listener.
   * */
  addEventListener<Event extends keyof Events>(event: Event, listener: Events[Event]): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const eventListeners = this.listeners.get(event)!;
    (eventListeners as Set<Events[Event]>).add(listener);
    return this.removeEventListener.bind(this, event, listener);
  }

  /**
   * Removes a listener for a specific event.
   * @param event - The event name.
   * @param listener - The listener function to remove.
   * */
  removeEventListener<Event extends keyof Events>(event: Event, listener: Events[Event]): void {
    const eventListeners = this.listeners.get(event);
    if (!eventListeners) {
      return;
    }
    (eventListeners as Set<Events[Event]>).delete(listener);
  }

  /**
   * Dispatches an event to all registered listeners.
   * @param event - The event name.
   * @param args - The arguments to pass to the listener functions.
   * */
  protected dispatchEvent<Event extends keyof Events>(event: Event, ...args: Parameters<Events[Event]>): void {
    const eventListeners = this.listeners.get(event);
    if (!eventListeners) {
      return;
    }
    (eventListeners as Set<Events[Event]>).forEach((listener) => listener(...args));
  }

  /**
   * Clears all event listeners.
   * */
  clear(): void {
    for (const [, eventListeners] of this.listeners) {
      eventListeners.clear();
    }
    this.listeners.clear();
  }
}
